import React from 'react';
import {observer} from 'mobx-react';
import Accordion from 'components/Accordion';
import Spinner from 'components/spinners/Spinner';
import RowViewModel from '../../state/RowViewModel';
import ActionLinks from '../../../components/ActionLinks';
import AccordionTableCell from './AccordionTableCell';

const AccordionTableRow = observer(({
  viewModel,
  level,
  columns,
  nestedRows,
  canExpand,
  onExpand,
  showLinks,
  localization,
  filter,
  folderColumns,
  openRemoveModal,
  onFolderExpanded,
  onFolderCollapsed,
  ...rest
}) => {
  const {customLinks, onlyShowCustomLinks, editRoute, onEditClicked, onRemove, horizontalScroll} = rest;
  const canExpandRow = canExpand ? canExpand(viewModel.data) : false;

  const handleClick = async () => {
    if (!canExpandRow) return;

    if (viewModel.isExpanded) {
      viewModel.collapse();
      if (viewModel.isFolder && onFolderCollapsed) {
        onFolderCollapsed(viewModel.data.id);
      }
    } else {
      viewModel.expand();
      if (viewModel.isFolder && onFolderExpanded) {
        onFolderExpanded(viewModel.data.id);
      }
      if (onExpand) {
        viewModel.setLoading(true);
        await onExpand(viewModel.data, filter);
        viewModel.setLoading(false);
      }
    }
  };

  const onRemoveClicked= onRemove ? () => openRemoveModal(viewModel.data) : undefined;
  const columnsToUse = viewModel.isFolder ? folderColumns : columns;

  const {editModel, removeModel} = localization;

  return (
    <React.Fragment>
      <div
        className={`AccordionTableRow border-indent border-indent-${level} flex align-items-middle ${canExpandRow ? 'clickable' : ''}`}
        onClick={handleClick}
      >
        {columnsToUse.map((column, index) => (
          <AccordionTableCell
            key={column.sortBy}
            column={column}
            viewModel={viewModel}
            level={level}
            canExpandRow={canExpandRow}
            isFirstColumn={index === 0}
            nestedRows={nestedRows}
            {...rest}
          />
        ))}
        {showLinks && (
          <div className={`AccordionTableCell ${horizontalScroll ? '' : 'accordion-col-1'}`} style={horizontalScroll ? {minWidth: '5rem'} : {}}>
            {!viewModel.isFolder && (
              <ActionLinks
                model={viewModel.data}
                action={editRoute || onEditClicked}
                onRemoveClicked={onRemoveClicked}
                editModelCaption={editModel}
                removeModelCaption={removeModel}
                customLinks={customLinks}
                onlyShowCustomLinks={onlyShowCustomLinks}
              />
            )}
          </div>
        )}
      </div>
      {canExpandRow && (
        <div className={`AccordionTableExpandedRow ${viewModel.isExpanded ? 'expanded' : ''}`} style={horizontalScroll ? {minWidth: 'fit-content'} : {}}>
          <div className='p0'>
            <Accordion active={viewModel.isExpanded}>
              {viewModel.isLoading ? (
                <div className='pt3' style={{height: 68}}><Spinner size='sm'/></div>
              ) : (
                nestedRows && nestedRows.map(({attribute, columns: nestedColumns}) => {
                  const items = viewModel.data[attribute] || [];
                  const columnsToUse = nestedColumns || columns;
                  return items.map((item, itemIndex) => (
                    <AccordionTableRow
                      key={`${attribute}-${itemIndex}`}
                      viewModel={new RowViewModel(item, viewModel.data)}
                      level={level + 1}
                      columns={columnsToUse}
                      nestedRows={nestedRows}
                      canExpand={canExpand}
                      onExpand={onExpand}
                      showLinks={showLinks}
                      localization={localization}
                      filter={filter}
                      openRemoveModal={openRemoveModal}
                      {...rest}
                    />
                  ));
                })
              )}
            </Accordion>
          </div>
        </div>
      )}
    </React.Fragment>
  );
});

export default AccordionTableRow;
